/**
 * AnswerPool — guess resolution, pool expansion, alternate title registration.
 * Preserved from v1 with ES module adaptation.
 *
 * Key design decisions:
 * - Slug-based map lookup (O(1) guess resolution)
 * - Multiple index keys per entry (no-hyphen, subtitle, number-words)
 * - Deduplication via _allKeysSet
 * - Lazy alternate title fetching (async, fire-and-forget)
 * - Separate found tracking per type (movie/actor/tv)
 * - resolve() returns all matches (a guess can match both a movie and a TV show)
 */

import { toGraphKey, toGraphKeyNoInitials, toGraphKeyNumWords, fuzzyMatch } from './fuzzy.js';
import { isValidMovie, isValidTv, ALT_TITLE_RE } from './validation.js';
import { tmdb, imgUrl } from './tmdb.js';

export class AnswerPool {
  constructor(excludedGenres) {
    this.excludedGenres = excludedGenres || new Set();
    // slug -> { id, type, label, image, ... }
    this._movieMap = new Map();
    this._actorMap = new Map();
    this._tvMap = new Map();
    // Dedup: all registered keys
    this._allKeysSet = new Set();
    // Found tracking
    this._foundMovies = new Set();
    this._foundActors = new Set();
    this._foundTv = new Set();
    // Name lookup for fuzzy matching
    this._movieNames = new Map(); // slug -> display name
    this._actorNames = new Map();
    this._tvNames = new Map();
    // Raw TMDB data for Director's Cut / expanded graph
    this._actorCredits = new Map(); // actorId -> credits array
    this._mediaCredits = new Map(); // mediaNodeId -> cast array
  }

  /** Register a movie into the pool */
  registerMovie(m) {
    if (!m || !m.id) return;
    const nodeId = 'm' + m.id;
    const label = m.title || m.name || '';
    const slug = toGraphKey(label);
    if (!slug) return;

    const entry = {
      id: m.id, nodeId, type: 'movie', label,
      image: imgUrl(m.poster_path, 'md'),
      poster_path: m.poster_path,
      vote_count: m.vote_count || 0,
      genre_ids: m.genre_ids || [],
      release_date: m.release_date || '',
    };

    this._addToMap(this._movieMap, this._movieNames, slug, label, entry);
    this._addVariants(this._movieMap, this._movieNames, label, entry);
  }

  /** Register a TV show into the pool */
  registerTv(t) {
    if (!t || !t.id) return;
    const nodeId = 't' + t.id;
    const label = t.name || t.title || '';
    const slug = toGraphKey(label);
    if (!slug) return;

    const entry = {
      id: t.id, nodeId, type: 'tv', label,
      image: imgUrl(t.poster_path, 'md'),
      poster_path: t.poster_path,
      vote_count: t.vote_count || 0,
      genre_ids: t.genre_ids || [],
      first_air_date: t.first_air_date || '',
    };

    this._addToMap(this._tvMap, this._tvNames, slug, label, entry);
    this._addVariants(this._tvMap, this._tvNames, label, entry);
  }

  /** Register an actor into the pool */
  registerActor(a) {
    if (!a || !a.id) return;
    const nodeId = 'a' + a.id;
    const label = a.name || '';
    const slug = toGraphKey(label);
    if (!slug) return;

    const entry = {
      id: a.id, nodeId, type: 'actor', label,
      image: imgUrl(a.profile_path, 'md'),
      profile_path: a.profile_path,
    };

    this._addToMap(this._actorMap, this._actorNames, slug, label, entry);
    this._addVariants(this._actorMap, this._actorNames, label, entry);
  }

  _addToMap(map, nameMap, slug, label, entry) {
    if (!this._allKeysSet.has(slug + ':' + entry.type)) {
      map.set(slug, entry);
      nameMap.set(slug, label);
      this._allKeysSet.add(slug + ':' + entry.type);
    }
  }

  _addVariants(map, nameMap, label, entry) {
    // No-hyphen variant
    const noHyphen = toGraphKey(label.replace(/-/g, ' '));
    if (noHyphen && noHyphen !== toGraphKey(label)) {
      this._addToMap(map, nameMap, noHyphen, label, entry);
    }

    // Subtitle variant (before colon/dash)
    if (ALT_TITLE_RE.test(label)) {
      const parts = label.split(/[:–—-]/);
      if (parts.length > 1) {
        const sub = toGraphKey(parts[0].trim());
        if (sub && sub.length > 2) {
          this._addToMap(map, nameMap, sub, label, entry);
        }
      }
    }

    // No-initials variant
    const noInit = toGraphKeyNoInitials(label);
    if (noInit) {
      this._addToMap(map, nameMap, noInit, label, entry);
    }

    // Number-to-words variant
    const numWords = toGraphKeyNumWords(label);
    if (numWords) {
      this._addToMap(map, nameMap, numWords, label, entry);
    }
  }

  /** Expand pool from actor's TMDB data */
  expandFromActor(tmdbData) {
    if (!tmdbData) return;
    const credits = tmdbData.combined_credits?.cast || [];
    this._actorCredits.set(tmdbData.id, credits);

    for (const c of credits) {
      if (c.media_type === 'movie' && isValidMovie(c, this.excludedGenres)) {
        this.registerMovie({ id: c.id, title: c.title, poster_path: c.poster_path, vote_count: c.vote_count, genre_ids: c.genre_ids, release_date: c.release_date });
      } else if (c.media_type === 'tv' && isValidTv(c, this.excludedGenres)) {
        this.registerTv({ id: c.id, name: c.name, poster_path: c.poster_path, vote_count: c.vote_count, genre_ids: c.genre_ids, first_air_date: c.first_air_date });
      }
    }
  }

  /** Expand pool from movie's TMDB data */
  expandFromMovie(tmdbData) {
    if (!tmdbData) return;
    const cast = (tmdbData.credits?.cast || []).filter(c => c.profile_path);
    this._mediaCredits.set('m' + tmdbData.id, cast);

    for (const c of cast) {
      this.registerActor({ id: c.id, name: c.name, profile_path: c.profile_path });
    }
  }

  /** Expand pool from TV show's TMDB data */
  expandFromTv(tmdbData) {
    if (!tmdbData) return;
    const cast = (tmdbData.aggregate_credits?.cast || []).filter(c => c.profile_path);
    this._mediaCredits.set('t' + tmdbData.id, cast);

    for (const c of cast) {
      this.registerActor({ id: c.id, name: c.name, profile_path: c.profile_path });
    }
  }

  /** Fetch and register alternate titles for movies (fire-and-forget) */
  fetchAltTitles(movieCredits) {
    for (const c of movieCredits) {
      if (!c.id || !c.title) continue;
      tmdb(`/movie/${c.id}/alternative_titles`).then(data => {
        const titles = data?.titles || [];
        for (const t of titles) {
          if (t.title && t.title !== c.title) {
            const entry = this._movieMap.get(toGraphKey(c.title));
            if (entry) {
              const slug = toGraphKey(t.title);
              if (slug) {
                this._addToMap(this._movieMap, this._movieNames, slug, t.title, entry);
              }
            }
          }
        }
      }).catch(() => {});
    }
  }

  /** Resolve a guess string to matching pool entries */
  resolve(input) {
    if (!input) return [];
    const slug = toGraphKey(input);
    if (!slug) return [];

    const matches = [];

    // Exact slug match
    const movie = this._movieMap.get(slug);
    if (movie) matches.push(movie);

    const actor = this._actorMap.get(slug);
    if (actor) matches.push(actor);

    const tv = this._tvMap.get(slug);
    if (tv) matches.push(tv);

    // If no exact match, try fuzzy matching
    if (matches.length === 0) {
      const movieFuzzy = fuzzyMatch(input, [...this._movieNames.values()]);
      if (movieFuzzy) {
        const m = this._movieMap.get(toGraphKey(movieFuzzy.match));
        if (m) matches.push(m);
      }

      const actorFuzzy = fuzzyMatch(input, [...this._actorNames.values()]);
      if (actorFuzzy) {
        const a = this._actorMap.get(toGraphKey(actorFuzzy.match));
        if (a) matches.push(a);
      }

      const tvFuzzy = fuzzyMatch(input, [...this._tvNames.values()]);
      if (tvFuzzy) {
        const t = this._tvMap.get(toGraphKey(tvFuzzy.match));
        if (t) matches.push(t);
      }
    }

    return matches;
  }

  /** Mark a node as found */
  markFound(nodeId) {
    if (nodeId.startsWith('m')) this._foundMovies.add(nodeId);
    else if (nodeId.startsWith('a')) this._foundActors.add(nodeId);
    else if (nodeId.startsWith('t')) this._foundTv.add(nodeId);
  }

  /** Check if a node is already found */
  isFound(nodeId) {
    if (nodeId.startsWith('m')) return this._foundMovies.has(nodeId);
    if (nodeId.startsWith('a')) return this._foundActors.has(nodeId);
    if (nodeId.startsWith('t')) return this._foundTv.has(nodeId);
    return false;
  }

  /** Get all found node IDs */
  getFoundNodes() {
    return new Set([...this._foundMovies, ...this._foundActors, ...this._foundTv]);
  }

  /** Get the expanded adjacency for Director's Cut */
  getExpandedAdjacency() {
    const adj = new Map();

    // For every actor with fetched credits, connect to their media
    for (const [actorId, credits] of this._actorCredits) {
      const actorNodeId = 'a' + actorId;
      if (!adj.has(actorNodeId)) adj.set(actorNodeId, new Set());

      for (const c of credits) {
        if (c.media_type === 'movie' && isValidMovie(c, this.excludedGenres)) {
          const mid = 'm' + c.id;
          if (!adj.has(mid)) adj.set(mid, new Set());
          adj.get(actorNodeId).add(mid);
          adj.get(mid).add(actorNodeId);
        } else if (c.media_type === 'tv' && isValidTv(c, this.excludedGenres)) {
          const tid = 't' + c.id;
          if (!adj.has(tid)) adj.set(tid, new Set());
          adj.get(actorNodeId).add(tid);
          adj.get(tid).add(actorNodeId);
        }
      }
    }

    // For every media with fetched cast, connect to their actors
    for (const [mediaNodeId, cast] of this._mediaCredits) {
      if (!adj.has(mediaNodeId)) adj.set(mediaNodeId, new Set());
      for (const c of cast) {
        const aid = 'a' + c.id;
        if (!adj.has(aid)) adj.set(aid, new Set());
        adj.get(mediaNodeId).add(aid);
        adj.get(aid).add(mediaNodeId);
      }
    }

    return adj;
  }

  /** Get pool entry by node ID */
  getEntry(nodeId) {
    if (!nodeId) return null;
    const prefix = nodeId[0];
    const numId = parseInt(nodeId.slice(1), 10);

    if (prefix === 'm') {
      for (const entry of this._movieMap.values()) {
        if (entry.id === numId) return entry;
      }
    } else if (prefix === 'a') {
      for (const entry of this._actorMap.values()) {
        if (entry.id === numId) return entry;
      }
    } else if (prefix === 't') {
      for (const entry of this._tvMap.values()) {
        if (entry.id === numId) return entry;
      }
    }
    return null;
  }

  /** Get stats about the pool */
  getStats() {
    return {
      movies: this._movieMap.size,
      actors: this._actorMap.size,
      tv: this._tvMap.size,
      foundMovies: this._foundMovies.size,
      foundActors: this._foundActors.size,
      foundTv: this._foundTv.size,
    };
  }
}
